import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";
import { displayName } from "../lib/displayName";

export function CommunityList() {
  const { user, profile } = useAuth();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", monthlyPriceUSD: 0, coverImageUrl: "" });
  const load = async () => { const { data } = await api.get("/communities"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/communities", { ...form, monthlyPriceUSD: parseFloat(form.monthlyPriceUSD) || 0 });
      setShow(false); setForm({ name: "", description: "", monthlyPriceUSD: 0, coverImageUrl: "" });
      load(); toast.success("Community created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const canHost = user?.role === "PROVIDER" && profile?.subscriptionStatus === "active";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mystic">Sacred Circles</div>
            <h1 className="font-serif text-5xl mt-2">Communities</h1>
          </div>
          {canHost && (
            <button onClick={() => setShow(!show)} className="bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs" data-testid="comm-create-toggle">+ New</button>
          )}
        </div>
        {show && (
          <div className="parchment p-6 space-y-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="comm-name" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="comm-desc" />
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="label-mystic">Monthly USD (0=free)</label>
                <input type="number" step="0.01" value={form.monthlyPriceUSD} onChange={(e) => setForm({...form, monthlyPriceUSD: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm mt-1" data-testid="comm-price" />
              </div>
              <ImageUploader value={form.coverImageUrl} onChange={(v) => setForm({...form, coverImageUrl: v})} testid="comm-cover" label="Cover" />
            </div>
            <button onClick={create} className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid="comm-create-btn">Create</button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => (
            <Link to={`/communities/${c.id}`} key={c.id} className="parchment p-6 hover:border-secondary/40 transition" data-testid={`comm-${c.id}`}>
              {c.coverImageUrl && <img src={c.coverImageUrl} alt="" className="w-full h-40 object-cover border border-secondary/15 mb-4" />}
              <h3 className="font-serif text-2xl">{c.name}</h3>
              <p className="text-foreground/70 text-sm line-clamp-2 mt-1">{c.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="font-serif text-xl text-secondary">{c.monthlyPriceUSD > 0 ? `$${c.monthlyPriceUSD.toFixed(2)}/mo` : "Free"}</div>
                <span className="text-xs text-foreground/60 flex items-center gap-1"><Users size={12} strokeWidth={1.25} /> {c.memberCount}</span>
              </div>
            </Link>
          ))}
          {items.length === 0 && <div className="parchment p-12 text-center text-foreground/50 italic col-span-2">No communities yet.</div>}
        </div>
      </div>
    </Layout>
  );
}

export function CommunityDetail() {
  const { cid } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [posts, setPosts] = useState([]);

  const load = async () => {
    try { const { data } = await api.get(`/communities/${cid}`); setItem(data); }
    catch { toast.error("Not found"); nav("/communities"); }
  };
  useEffect(() => { load(); }, [cid]);
  useEffect(() => { if (item?.isMember) api.get(`/communities/${cid}/posts`).then(({data}) => setPosts(data)).catch(() => {}); }, [item?.isMember, cid]);
  useEffect(() => {
    if (!sessionId) return;
    const poll = async (n=0) => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Membership active"); await refresh(); load(); return; }
        if (n >= 8) return;
        setTimeout(() => poll(n+1), 2000);
      } catch {}
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const join = async () => {
    if (!item) return;
    if (item.monthlyPriceUSD > 0) {
      try {
        const { data } = await api.post("/checkout/init", {
          origin: window.location.origin, kind: "community_membership", communityId: cid,
        });
        window.location.href = data.url;
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    } else {
      try { await api.post(`/communities/${cid}/join`); load(); toast.success("Joined"); }
      catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    }
  };

  if (!item) return <Layout><div className="parchment p-12 text-center">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="parchment overflow-hidden">
        {item.coverImageUrl && <img src={item.coverImageUrl} alt="" className="w-full h-56 object-cover" />}
        <div className="p-8">
          <div className="label-mystic">Hosted by {displayName({ ...(item.ownerProfile || {}), user: item.owner })}</div>
          <h1 className="font-serif text-5xl mt-2 mb-3">{item.name}</h1>
          <p className="text-foreground/80 mb-6">{item.description}</p>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="font-serif text-3xl text-secondary">{item.monthlyPriceUSD > 0 ? `$${item.monthlyPriceUSD.toFixed(2)}/mo` : "Free"}</div>
            {item.isMember || item.ownerId === user?.id ? (
              <span className="text-secondary uppercase tracking-wider text-xs" data-testid="comm-member-status">✦ Active member</span>
            ) : (
              <button onClick={join} className="bg-primary text-primary-foreground px-8 py-3 rounded-sm uppercase tracking-wider text-xs" data-testid="comm-join-btn">
                {item.monthlyPriceUSD > 0 ? "Join · Subscribe" : "Join Free"}
              </button>
            )}
          </div>
          <div className="mt-4 label-mystic flex items-center gap-2"><Users size={12} strokeWidth={1.25} /> {item.memberCount} members</div>
        </div>
      </div>
      {item.isMember && (
        <div className="mt-6">
          <div className="label-mystic mb-3">Community Posts</div>
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="parchment p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-serif text-secondary">{p.user?.name}</span>
                  <span className="label-mystic">{new Date(p.createdAt).toLocaleString()}</span>
                </div>
                <p className="text-foreground/85 whitespace-pre-wrap">{p.content}</p>
              </div>
            ))}
            {posts.length === 0 && <div className="parchment p-8 text-foreground/50 italic text-center">No posts yet.</div>}
          </div>
        </div>
      )}
    </Layout>
  );
}
